import React, { useState } from 'react';
import {
	GroupMember,
	useLazyGroupMembersQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
// @ts-ignore
import styles from './PatientsGroupsMembers.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import MessageRecipientsMultiSelectWithCheckListField from '../../../Base/Components/Inputs/MessageRecipientsMultiSelectWithCheckList/MessageRecipientsMultiSelectWithCheckListField';
import { useForm } from 'react-hook-form';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import useGroupMembers from '../../Hooks/useGroupMembers';
import useFlatData from '../../../Base/Hooks/useFlatData';
import FlatList from '../../../Base/Components/FlatList';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import UserAvatarImage from '../../../../Components/UserAvatar/UserAvatarImage';
import useTranslation from '../../../../Hooks/useTranslation';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import useNotification from '../../../../Hooks/useNotification.hook';
import PatientsGroupsAddMembersByImportModal from '../PatientsGroupsAddMembersByImport/PatientsGroupsAddMembersByImportModal';

interface Props {
	groupId: string;
}

interface IPatientsGroupsMembersItem {
	item: GroupMember;
	groupId: string;
	onRemove: () => void;
}

const PatientsGroupsMembersItem: React.FC<IPatientsGroupsMembersItem> = ({
	item,
	groupId,
	onRemove,
}) => {
	const { displayError } = useApiClient();
	const { t } = useTranslation();

	const handleRemoveUserFromGroup = async () => {
		try {
			await apiClient.delete(`/group/${groupId}/member/${item.id}`);
		} catch (e) {
			displayError(e);
		}
		onRemove();
	};

	return (
		<div className={styles.item}>
			<UserAvatarImage
				userId={item.id}
				profileImageUrl={item?.user?.profileImageUrl ?? ''}
				isDoctor={item?.user?.isDoctor ?? false}
				createdAt={item?.user?.avatarFile?.createdAt}
				customClass={styles.image}
			/>
			<div className={styles.content}>
				<div className={styles.name}>{item?.user?.profile?.fullName}</div>
				<div className={styles.text}>
					{item?.user?.profile?.birthNumber && (
						<>
							<Text tx='base.birthNumberShort' inline firstUpper />:{' '}
							{item?.user?.profile?.birthNumber}
						</>
					)}
					{item?.user?.profile?.age && (
						<span>
							, {item?.user?.profile?.age} {t('base.years.many')}
						</span>
					)}
					{item?.user?.formattedPhoneNumber && (
						<span>, {item?.user?.formattedPhoneNumber}</span>
					)}
				</div>
				{item?.user?.userRelations?.map(userRelation => {
					return (
						<div className={styles.text}>
							<span>
								{userRelation?.workplace?.name} -{' '}
								{userRelation?.user?.profile?.fullName}
							</span>
						</div>
					);
				})}
			</div>
			<div className={styles.controls}>
				<RemoveButton
					onClick={handleRemoveUserFromGroup}
					confirmationTitle={t('component.groupMsgs.removePatient')}
					confirmationText={t('component.userGroups.removeUser', {
						userName: item?.user?.profile?.fullName,
					})}
					flashMessage='component.groupMsgs.userRemoved'
					firstUpper
				/>
			</div>
		</div>
	);
};
const WRAPPER_ID = 'patientsGroupMembersWrapper';

const PatientsGroupsMembers: React.FC<Props> = ({ groupId }) => {
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [getGroupMembersQuery, { data, isLoading }] =
		useLazyGroupMembersQuery();
	const { getGroupMembers, setPage, page, getNextPage } =
		useGroupMembers(getGroupMembersQuery);
	const { flatData, hasMoreData, loadData, setFilter } = useFlatData(
		getGroupMembers,
		{
			data: data?.groupMembers?.data,
			totalPages: data?.groupMembers?.meta?.totalPages,
			page: data?.groupMembers?.meta?.page,
		},
		page,
		setPage,
		{
			groupId: groupId,
			pagination: {
				take: 20,
			},
		},
	);

	const [isImportOpen, setIsImportOpen] = useState(false);

	const formMethods = useForm({});

	if (isLoading) {
		return <Loading />;
	}

	const handleAfterImport = () => {
		loadData();
		setIsImportOpen(false);
	};

	const handleAddMember = async (values: any) => {
		values?.members?.forEach(async (item: SelectValue) => {
			try {
				const result = await apiClient.post(`/group/${groupId}/member`, {
					groupId: groupId,
					userId: item.value,
				});
				if (result) {
					showNotificationSuccess({
						content: t('component.groupMsgs.memberAdded'),
					});
					formMethods.reset({});
					loadData();
				}
			} catch (e) {
				displayError(e);
			}
		});
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.addUserWrapper}>
				<div className={styles.header}>
					<Text className={styles.title} tx='base.addUsersToGroup' firstUpper />
					<LinkButton
						title='base.addFromCSV'
						theme={Theme.BLUE}
						firstUpper
						onClick={() => setIsImportOpen(true)}
					/>
				</div>
				<div>
					<FormWrapper formMethods={formMethods}>
						<div className={styles.inputs}>
							<MessageRecipientsMultiSelectWithCheckListField
								label='base.selectPatientsToAddToGroup'
								placeholder='component.groupMsgs.searchMember'
								enableGroups={false}
								className={styles.select}
								name='members'
								isRequired
								portal
							/>
							<SubmitButton
								onSubmit={handleAddMember}
								title='base.shared.add'
							/>
						</div>
					</FormWrapper>
				</div>
			</div>
			<div className={styles.membersWrapper}>
				<div className={styles.header}>
					<Text tx='base.usersInGroup' className={styles.title} />
					<div className={styles.counter}>
						<Text tx='component.groupMsgs.amountOfMembers' inline firstUpper />:{' '}
						{data?.groupMembers?.meta?.count}
					</div>
				</div>
				<ScrollContainer wrapperId={WRAPPER_ID}>
					<FlatList
						onRefresh={loadData}
						onLoadMore={getNextPage}
						data={flatData}
						isLoading={isLoading}
						hasMoreData={hasMoreData}
						scrollableTargetId={WRAPPER_ID}
						itemKey={'id'}
						renderItem={item => (
							<PatientsGroupsMembersItem
								item={item}
								groupId={groupId}
								onRemove={loadData}
							/>
						)}
					/>
				</ScrollContainer>
			</div>
			{isImportOpen && (
				<PatientsGroupsAddMembersByImportModal
					isOpen
					onClose={() => setIsImportOpen(false)}
					onSuccess={handleAfterImport}
					groupId={groupId}
				/>
			)}
		</div>
	);
};

export default PatientsGroupsMembers;
